import { get, post } from './request';

export interface HealthCheckDefinition {
  id: string;
  type: string;
  label: string;
  meta: Record<string, unknown>;
  endpoint: string;
}

export interface HealthCheckGroupConfig {
  rowLabel?: string;
}

export interface HealthCheckGroup {
  title: string;
  layout?: 'matrix' | 'list';
  config?: HealthCheckGroupConfig;
  checks: HealthCheckDefinition[];
}

export interface HealthChecksResponse {
  courseId: number;
  checks: HealthCheckGroup[];
}

export interface HealthCheckMetric {
  label: string;
  value: number | string;
}

export interface HealthCheckProgressResponse {
  ref?: number;
  dialectId?: number;
  totals?: Record<string, number>;
  metrics: HealthCheckMetric[];
  completionPercent: number;
  status: string;
}

export const fetchHealthChecks = async (courseId: number) => {
  return await get<HealthChecksResponse>(`/courses/${courseId}/health/checks`);
};

export const fetchHealthCheckProgress = async (endpoint: string) => {
  return await get<HealthCheckProgressResponse>(endpoint);
};

export type PublishGateStatus = 'green' | 'warning' | 'red' | 'no-run';

export interface PublishGateCheck {
  checkId: string;
  status: string;
  completionPercent: number;
  // Real total of pending elements; pendingIds is capped at 100 actionable ids.
  pendingCount?: number;
  pendingIds: number[];
  note?: string;
}

export interface PublishGateResource {
  ref: number;
  name: string;
  status: 'green' | 'warning' | 'red';
  blockers: PublishGateCheck[];
  warnings: PublishGateCheck[];
  excepted: PublishGateCheck[];
}

export interface PublishGateRun {
  runId: number;
  createdAt: string;
  totalChecks: number;
  withIssues: number;
}

export interface PublishGate {
  courseId: number;
  run: PublishGateRun | null;
  status: PublishGateStatus;
  resources: PublishGateResource[];
  note?: string;
}

export interface PersistRunResult {
  runId: number;
  total: number;
  withIssues: number;
  durationMs: number;
  gate: PublishGate;
}

export const fetchPublishGate = async (courseId: number) => {
  return await get<PublishGate>(`/courses/${courseId}/health/publish-gate`);
};

// Runs EVERY health check of the course server-side and stores the verdict the gate reads.
// Slow call (tens of seconds on a big course): no payload, response includes the fresh gate.
export const persistHealthRun = async (courseId: number) => {
  const result = await post<PersistRunResult>(`/courses/${courseId}/health/persist-run`, {});
  return result.data ?? null;
};
